import { BehaviorSubject } from 'rxjs';
import { Injectable } from '@angular/core';
import { MessageService } from 'primeng/api';
import { ErrorService } from './error.service';
import { commonConstants } from '../constants/common.constants';
import { TranslocoService } from '@ngneat/transloco';
import { HttpErrorResponse } from '@angular/common/http';

@Injectable({
  providedIn: 'root',
})
/**
 * Notification service class
 * This class provides methods to add single, multiple alerts as a toast
 * NOTE: this should be the only class that lives in core, even if this should live in the other library: ui-common
 */
export class NotificationService {
  logoutMessage$ = new BehaviorSubject<any>(null);
  showLogoutMessage$ = new BehaviorSubject<boolean>(false);

  constructor(
    private messageService: MessageService,
    private errorService: ErrorService,
    private translateService: TranslocoService
  ) {}

  /**
   * add single toast message
   *
   * @param severity Severity level of the message, valid values are "success", "info", "warn" and "error"
   * @param summary Summary text of the message.
   * @param detail Detail text of the message.
   * @param sticky If true the message will remain in screen until user close it up.
   */
  addSingle(severity: string, summary: string, detail: string, sticky = false) {
    this.messageService.add({ severity, summary, detail, sticky });
  }

  /**
   * add multiple toast messages
   *
   * @param messages
   * array of message type {severity:'success', summary:'Service Message', detail:'Via MessageService'}
   */
  addMultiple(messages: any) {
    this.messageService.addAll(messages);
  }

  /**
   * clear all toast messages
   */
  clear() {
    this.messageService.clear();
  }

  success(summary: string, detail = '') {
    this.addSingle(commonConstants.SUCCESS, summary, detail);
  }

  info(summary: string, detail = '') {
    this.addSingle(commonConstants.INFO, summary, detail);
  }

  warning(summary: string, detail = '', sticky = false) {
    this.addSingle(commonConstants.WARNING, summary, detail, sticky);
  }

  show(configuration: any) {
    this.messageService.add(configuration);
  }

  showErrorMessage(summary: string, detail = '', stacktrace: any = false) {
    const errorObj = {
      detail,
      summary,
      sticky: true,
      data: stacktrace,
      severity: commonConstants.ERROR,
    };

    this.showLogoutMessage$.next(true);
    this.logoutMessage$.next(errorObj);
  }

  error(summary: string, detail = '', stacktrace: any = false) {
    this.messageService.add({
      detail,
      summary,
      sticky: true,
      data: stacktrace,
      severity: commonConstants.ERROR,
    });
  }

  serverError(error: HttpErrorResponse) {
    if (!error?.status) {
      return this.error(
        this.translateService.translate(
          this.errorService.getErrorMessage(error)
        ),
        this.translateService.translate(
          this.errorService.getFriendlyDetails(error)
        ),
        error
      );
    }

    return this.error(
      this.translateService.translate(this.errorService.getErrorMessage(error)),
      this.translateService.translate(
        this.errorService.getFriendlyDetails(error)
      ),
      error
    );
  }
}
